/**
 * RCCで使うもの and SSR
 */

'use client';

import { ApolloLink, HttpLink, split } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
  SSRMultipartLink,
} from '@apollo/experimental-nextjs-app-support';
import { createUploadLink } from 'apollo-upload-client';
import { createClient } from 'graphql-ws';

import { getCurrentIdToken } from '@/lib/auth/token-bridge';
import { createConnectTimeoutFetch } from '@/lib/ws/connect-timeout-fetch';
import { createConnectTimeoutWebSocket } from '@/lib/ws/connect-timeout-websocket';
import { describeCloseEvent, recordWsEvent } from '@/lib/ws/ws-telemetry';

import { createAuthLink } from './auth-link';
import { sentryLink } from './sentry-link';
import { SSELink } from './sse-link';
import { createSubscriptionLink } from './subscription-link';

// WebSocket の connection_init と、SSE のリクエストヘッダーの両方で使う認証情報
function getAuthorizationParams() {
  const token = getCurrentIdToken() ?? '';

  if (!token) {
    throw new Error('No authentication token available');
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

const GRAPHQL_ENDPOINT = `${process.env.NEXT_PUBLIC_API_ENDPOINT}/graphql`;

export function makeClient() {
  const uploadLink = createUploadLink({
    uri: GRAPHQL_ENDPOINT,
    fetchOptions: { cache: 'no-store' },
  });

  const httpLink = new HttpLink({
    uri: GRAPHQL_ENDPOINT,
    fetchOptions: { cache: 'no-store' },
  });

  // iOS Safari / PWA でバックグラウンドサスペンドから復帰した際、
  // WebSocket が dead 状態のまま onClose/onError が発火しないケースに対応するため、
  // ライフサイクルハンドラと積極的な再接続戦略を実装する。
  const wsLink =
    typeof window !== 'undefined'
      ? new GraphQLWsLink(
          createClient({
            url: GRAPHQL_ENDPOINT,
            // 接続を作る段階（CONNECTING）に待ち時間を付ける。graphql-ws 自体には無く、
            // 詰まった接続の試行は全購読で共有されるため、放置すると以降の送信がすべて待たされる。
            webSocketImpl: createConnectTimeoutWebSocket({
              onConnectTimeout: ({ timeoutMs, consecutiveTimeouts }) =>
                recordWsEvent('connect_timeout', { timeoutMs, consecutiveTimeouts, transport: 'ws' }),
            }),
            // iOS の凍結を早めに検知するため keepAlive を短縮 (10s -> 5s)
            keepAlive: 5000,
            // 接続が開いた後の応答待ち。開く前の待ち時間は webSocketImpl 側で扱う
            connectionAckWaitTimeout: 10000,
            // subscription が短時間で再 subscribe される際の race を回避するため
            // 即時クローズではなく短いグレース期間を設ける
            lazyCloseTimeout: 500,
            // Apollo Client が再 subscribe した時に同じ接続を再利用させる
            retryAttempts: Infinity,
            // 指数バックオフ (最大 10s) で再接続を粘り強く試みる
            retryWait: async retries => {
              const backoff = Math.min(1000 * 2 ** retries, 10000);
              const jitter = Math.floor(Math.random() * 300);
              await new Promise(resolve => setTimeout(resolve, backoff + jitter));
            },
            // 4401 (Unauthorized) のみ再試行しない。それ以外は全てリトライ。
            shouldRetry: errOrCloseEvent => {
              if (errOrCloseEvent instanceof CloseEvent) {
                return errOrCloseEvent.code !== 4401;
              }
              return true;
            },
            // 接続の動きは本番でも Sentry のブレッドクラムに残す（lib/ws/ws-telemetry.ts）
            on: {
              connecting: isRetry => recordWsEvent('connecting', { isRetry, transport: 'ws' }),
              opened: () => recordWsEvent('opened', { transport: 'ws' }),
              connected: () => recordWsEvent('connected', { transport: 'ws' }),
              closed: closeEvent => {
                recordWsEvent('closed', { ...describeCloseEvent(closeEvent), transport: 'ws' });
                // iOS のバックグラウンド復帰時に close が遅延発火するケースを
                // 観測できるよう開発時のみログ出力。
                if (process.env.NODE_ENV === 'development') {
                  console.info('[graphql-ws] connection closed', closeEvent);
                }
              },
              error: err => {
                recordWsEvent('error', { transport: 'ws' });
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[graphql-ws] connection error', err);
                }
              },
            },
            connectionParams: getAuthorizationParams,
          }),
        )
      : null;

  // 購読を SSE（POST + text/event-stream）で受けるリンク。どちらで受けるかは getSubscriptionTransport() が決める。
  // 通常の API 呼び出しと同じ HTTP/2 接続に載せて、新しい WebSocket 接続を作らずに済ませる狙い。
  const sseLink =
    typeof window !== 'undefined'
      ? new SSELink({
          url: GRAPHQL_ENDPOINT,
          headers: getAuthorizationParams,
          fetchFn: createConnectTimeoutFetch({
            onConnectTimeout: ({ timeoutMs, consecutiveTimeouts }) =>
              recordWsEvent('connect_timeout', { timeoutMs, consecutiveTimeouts, transport: 'sse' }),
            // WebSocket の opened と connection_ack に当たる。SSE はヘッダーが届いた時点で、接続済みとして扱う
            onResponseHeaders: () => {
              recordWsEvent('opened', { transport: 'sse' });
              recordWsEvent('connected', { transport: 'sse' });
            },
          }),
        })
      : null;

  // NOTE: iOS Safari / PWA のバックグラウンド復帰検知は、以下の二段構えで対応する。
  //   1. graphql-ws 内蔵の keepAlive (5s) + shouldRetry + retryAttempts=Infinity
  //      → フォアグラウンド復帰後の ping 失敗を検知して自動再接続する
  //   2. 各 chat フックの useChatStreamingTimeout (initial 120s / idle 30s)
  //      → 万一再接続できなくても永久 Thinking を防ぐ最後の砦
  // visibilitychange を Apollo Client 層で扱うと「リスナー解除のライフサイクルが
  // makeClient の生存に紐付かない（解除タイミングが無い）」「dispatch 先が
  // 不在になりやすい」など弊害が大きいため、本層では実装しない。

  const subscriptionLink = wsLink && sseLink ? createSubscriptionLink(sseLink, wsLink) : wsLink;

  const splitLink = subscriptionLink
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        subscriptionLink,
        uploadLink,
      )
    : uploadLink;

  const authLink = createAuthLink();

  // Sentry error linkを最初に配置して、すべてのエラーをキャプチャ
  const clientSideLink = ApolloLink.from([sentryLink, authLink.concat(splitLink)]);

  // サーバーサイド用のリンクチェーン（SSR時もエラー監視）
  const serverSideLink = ApolloLink.from([sentryLink, new SSRMultipartLink({ stripDefer: true }), httpLink]);

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        LegendStory: {
          keyFields: story => {
            return `${story.legendId}:${story.storyId}:${story.duration}`;
          },
        },
        Principle: {
          keyFields: principle => {
            return `${principle.id}:${principle.principle}`;
          },
        },
        // ChatSession は id を持たないため chatbotId+sessionId の複合キーで正規化する。
        // これにより cache-and-network でセッション一覧を即表示しつつ最新化できる。
        ChatSession: {
          keyFields: ['chatbotId', 'sessionId'],
        },
        // ChatGranularityTokenCount は user/department/organization の3配列で
        // 同一の startDate:endDate を持つため、複合キー正規化すると相互に上書きしてデータ欠損する。
        // 一意キーが存在しないので正規化せず、親(ChatTokenCount)に埋め込んで保持する。
        ChatGranularityTokenCount: {
          keyFields: false,
        },
      },
    }),
    link: typeof window === 'undefined' ? serverSideLink : clientSideLink,
    devtools: {
      enabled: process.env.NODE_ENV === 'development',
    },
  });
}

export function ApolloSSRProvider({ children }: React.PropsWithChildren) {
  return <ApolloNextAppProvider makeClient={makeClient}>{children}</ApolloNextAppProvider>;
}
